import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { DashboardLayout } from '../layouts/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { 
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import { 
    FolderOpen,
    Plus, 
    Pencil, 
    Trash2,
    Loader2,
    Upload,
    ImageIcon,
    Gift,
    GripVertical
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getImageUrl = (image) => {
    if (!image) return null;
    if (image.startsWith('http')) return image;
    return `${process.env.REACT_APP_BACKEND_URL}${image}`;
};

export const AdminRewardSectionsPage = () => {
    const { token } = useAuth();
    const { tenant } = useTenant();
    
    const [sections, setSections] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingSection, setEditingSection] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [deleteId, setDeleteId] = useState(null);
    const [uploadingImage, setUploadingImage] = useState(null);
    
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        display_order: 0
    });

    useEffect(() => {
        fetchSections();
    }, []);

    const fetchSections = async () => {
        try {
            const response = await axios.get(`${API}/admin/reward-sections`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setSections(response.data);
        } catch (error) {
            console.error('Failed to fetch sections:', error);
            toast.error('Kategorien konnten nicht geladen werden');
        } finally {
            setLoading(false);
        }
    };

    const openCreateDialog = () => {
        setEditingSection(null);
        setFormData({
            name: '',
            description: '',
            display_order: sections.length
        });
        setDialogOpen(true);
    };

    const openEditDialog = (section) => {
        setEditingSection(section);
        setFormData({
            name: section.name,
            description: section.description || '',
            display_order: section.display_order || 0
        });
        setDialogOpen(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.name.trim()) {
            toast.error('Bitte geben Sie einen Namen ein');
            return;
        }

        setSubmitting(true);
        try {
            if (editingSection) {
                await axios.put(
                    `${API}/admin/reward-sections/${editingSection.id}`,
                    formData,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                toast.success('Kategorie aktualisiert');
            } else {
                await axios.post(
                    `${API}/admin/reward-sections`,
                    formData,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                toast.success('Kategorie erstellt');
            }
            
            setDialogOpen(false);
            fetchSections();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Speichern fehlgeschlagen');
        } finally {
            setSubmitting(false);
        }
    };

    const handleImageUpload = async (sectionId, file) => {
        if (!file) return;
        
        const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/gif'];
        if (!allowedTypes.includes(file.type)) {
            toast.error('Nicht unterstütztes Dateiformat');
            return;
        }
        
        if (file.size > 5 * 1024 * 1024) {
            toast.error('Datei ist zu groß (max. 5MB)');
            return;
        }
        
        setUploadingImage(sectionId);
        
        try {
            const formDataUpload = new FormData();
            formDataUpload.append('file', file);
            
            await axios.post(
                `${API}/admin/reward-sections/${sectionId}/upload-image`,
                formDataUpload,
                { 
                    headers: { 
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data'
                    } 
                }
            );
            
            toast.success('Bild hochgeladen');
            fetchSections();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Upload fehlgeschlagen');
        } finally {
            setUploadingImage(null);
        }
    };

    const handleDelete = async (sectionId) => {
        try {
            await axios.delete(`${API}/admin/reward-sections/${sectionId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Kategorie gelöscht');
            setDeleteId(null);
            fetchSections();
        } catch (error) {
            toast.error('Kategorie konnte nicht gelöscht werden');
        }
    };

    return (
        <DashboardLayout>
            <div className="space-y-8 animate-fade-in">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h1 className="text-3xl font-bold text-slate-900">Prämien-Kategorien</h1>
                        <p className="text-slate-600 mt-1">
                            Organisieren Sie Ihre Prämien in übersichtliche Kategorien
                        </p>
                    </div>
                    <Button
                        style={{ backgroundColor: tenant?.primary_color }}
                        onClick={openCreateDialog}
                        data-testid="create-section-btn"
                    >
                        <Plus size={18} className="mr-2" />
                        Neue Kategorie
                    </Button>
                </div>

                {/* Sections List */}
                {loading ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {[1, 2, 3].map(i => (
                            <Card key={i} className="animate-pulse">
                                <CardContent className="p-4">
                                    <div className="w-full h-32 bg-slate-200 rounded-xl mb-4" />
                                    <div className="h-6 bg-slate-200 rounded w-3/4 mb-2" />
                                    <div className="h-4 bg-slate-200 rounded w-1/2" />
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                ) : sections.length === 0 ? (
                    <Card>
                        <CardContent className="p-12 text-center">
                            <FolderOpen size={64} className="mx-auto text-slate-300 mb-4" />
                            <h3 className="text-xl font-semibold text-slate-900 mb-2">
                                Noch keine Kategorien
                            </h3>
                            <p className="text-slate-500 mb-6">
                                Erstellen Sie Kategorien, um Ihre Prämien zu organisieren
                            </p>
                            <Button
                                style={{ backgroundColor: tenant?.primary_color }}
                                onClick={openCreateDialog}
                            >
                                <Plus size={18} className="mr-2" />
                                Erste Kategorie erstellen
                            </Button>
                        </CardContent>
                    </Card>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="sections-list">
                        {sections.map((section) => (
                            <Card key={section.id} className="card-hover overflow-hidden" data-testid={`section-card-${section.id}`}>
                                <CardContent className="p-0">
                                    {/* Image Section */}
                                    <div className="relative group">
                                        {section.image ? (
                                            <div className="w-full h-36 bg-slate-100">
                                                <img 
                                                    src={getImageUrl(section.image)} 
                                                    alt={section.name}
                                                    className="w-full h-full object-cover"
                                                />
                                                {/* Overlay on hover */}
                                                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                                                    <Button
                                                        size="sm"
                                                        variant="secondary"
                                                        onClick={() => {
                                                            const input = document.createElement('input');
                                                            input.type = 'file';
                                                            input.accept = 'image/*';
                                                            input.onchange = (e) => handleImageUpload(section.id, e.target.files[0]);
                                                            input.click();
                                                        }}
                                                    >
                                                        <Upload size={14} className="mr-1" />
                                                        Ändern
                                                    </Button>
                                                </div>
                                            </div>
                                        ) : (
                                            <div 
                                                className="w-full h-36 flex flex-col items-center justify-center cursor-pointer hover:bg-slate-100 transition-colors"
                                                style={{ backgroundColor: `${tenant?.primary_color}08` }}
                                                onClick={() => {
                                                    const input = document.createElement('input');
                                                    input.type = 'file';
                                                    input.accept = 'image/*';
                                                    input.onchange = (e) => handleImageUpload(section.id, e.target.files[0]);
                                                    input.click();
                                                }}
                                            >
                                                {uploadingImage === section.id ? (
                                                    <Loader2 size={32} className="animate-spin text-slate-400" />
                                                ) : (
                                                    <>
                                                        <ImageIcon size={32} className="text-slate-300 mb-2" />
                                                        <span className="text-sm text-slate-400">Vorschaubild hinzufügen</span>
                                                    </>
                                                )}
                                            </div>
                                        )}
                                        
                                        {/* Order Badge */}
                                        <div className="absolute top-2 left-2">
                                            <Badge variant="secondary" className="bg-white/90 backdrop-blur-sm">
                                                <GripVertical size={12} className="mr-1" />
                                                Position {section.display_order + 1}
                                            </Badge>
                                        </div>
                                    </div>
                                    
                                    {/* Content Section */}
                                    <div className="p-4">
                                        <h3 className="text-lg font-semibold text-slate-900 mb-1">
                                            {section.name}
                                        </h3>
                                        {section.description && (
                                            <p className="text-sm text-slate-500 mb-3 line-clamp-2">
                                                {section.description}
                                            </p>
                                        )}
                                        
                                        <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                                            <div className="flex items-center gap-2">
                                                <Gift size={16} style={{ color: tenant?.primary_color }} />
                                                <span className="text-sm text-slate-600">
                                                    {section.reward_count || 0} Prämien
                                                </span>
                                            </div>
                                            <div className="flex gap-1">
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => openEditDialog(section)}
                                                    data-testid={`edit-section-${section.id}`}
                                                >
                                                    <Pencil size={16} />
                                                </Button>
                                                <Dialog open={deleteId === section.id} onOpenChange={(open) => !open && setDeleteId(null)}>
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className="text-red-500 hover:text-red-600 hover:bg-red-50"
                                                        onClick={() => setDeleteId(section.id)}
                                                        data-testid={`delete-section-${section.id}`}
                                                    >
                                                        <Trash2 size={16} />
                                                    </Button>
                                                    {deleteId === section.id && (
                                                        <Dialog open={true} onOpenChange={() => setDeleteId(null)}>
                                                            <DialogContent>
                                                                <DialogHeader>
                                                                    <DialogTitle>Kategorie löschen</DialogTitle>
                                                                    <DialogDescription>
                                                                        Möchten Sie "{section.name}" wirklich löschen? 
                                                                        Die zugehörigen Prämien werden nicht gelöscht, sondern nur aus der Kategorie entfernt.
                                                                    </DialogDescription>
                                                                </DialogHeader>
                                                                <DialogFooter>
                                                                    <Button variant="outline" onClick={() => setDeleteId(null)}>
                                                                        Abbrechen
                                                                    </Button>
                                                                    <Button 
                                                                        variant="destructive"
                                                                        onClick={() => handleDelete(section.id)}
                                                                        data-testid="confirm-delete-btn"
                                                                    >
                                                                        Löschen
                                                                    </Button>
                                                                </DialogFooter>
                                                            </DialogContent>
                                                        </Dialog>
                                                    )}
                                                </Dialog>
                                            </div>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                )}

                {/* Create/Edit Dialog */}
                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>
                                {editingSection ? 'Kategorie bearbeiten' : 'Neue Kategorie'}
                            </DialogTitle>
                            <DialogDescription>
                                {editingSection 
                                    ? 'Aktualisieren Sie die Kategoriedetails'
                                    : 'Erstellen Sie eine neue Kategorie für Ihre Prämien'
                                }
                            </DialogDescription>
                        </DialogHeader>
                        
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="name">Name *</Label>
                                <Input
                                    id="name"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    placeholder="z.B. Gutscheine, Erlebnisse, Technik..."
                                    data-testid="section-name-input"
                                />
                            </div>
                            
                            <div className="space-y-2">
                                <Label htmlFor="description">Beschreibung</Label>
                                <textarea
                                    id="description"
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    placeholder="Kurze Beschreibung der Kategorie..."
                                    className="w-full min-h-[80px] px-3 py-2 text-sm border rounded-md border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    data-testid="section-description-input"
                                />
                            </div>
                            
                            <div className="space-y-2">
                                <Label htmlFor="order">Anzeigereihenfolge</Label>
                                <Input
                                    id="order"
                                    type="number"
                                    min="0"
                                    value={formData.display_order}
                                    onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })}
                                    placeholder="0"
                                    data-testid="section-order-input"
                                />
                                <p className="text-xs text-slate-500">
                                    Kategorien werden nach dieser Nummer aufsteigend sortiert
                                </p>
                            </div>
                            
                            {editingSection && (
                                <div className="p-3 bg-slate-50 rounded-lg text-sm text-slate-500">
                                    Tipp: Sie können nach dem Speichern ein Vorschaubild hinzufügen
                                </div>
                            )}
                            
                            <DialogFooter className="pt-4">
                                <Button 
                                    type="button" 
                                    variant="outline" 
                                    onClick={() => setDialogOpen(false)}
                                >
                                    Abbrechen
                                </Button>
                                <Button
                                    type="submit"
                                    style={{ backgroundColor: tenant?.primary_color }}
                                    disabled={submitting}
                                    data-testid="save-section-btn"
                                >
                                    {submitting ? (
                                        <>
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                            Speichern...
                                        </>
                                    ) : (
                                        editingSection ? 'Aktualisieren' : 'Erstellen'
                                    )}
                                </Button>
                            </DialogFooter>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>
        </DashboardLayout>
    );
};
